// 昼夜（リアルタイム連動）。v0.6.0 の追加システム。
//
// 実時刻（ロケール時刻）の「時」から 4 つの時間帯（朝/昼/夕/夜）を判定する純関数。
// 描画は帯に応じて空のグラデ・環境光・星の濃さ・発光の強さを変える。客には昼型/夜型の
// 来店係数を与え、帯によって来やすさが変わる（決定的）。
//
// 決定性の担保: simulate は各来店刻みの絶対時刻（atMs）から帯を導く。atMs は
// state.lastTickMs + 経過秒で決まるので、同じ state + 経過からは必ず同じ帯になり、
// 刻みの分割にも依存しない（合成可能）。Date.now() はここでは呼ばない。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

/** 1 日の時間帯。 */
export type TimeOfDay = 'morning' | 'day' | 'dusk' | 'night';

/** 時間帯の全列挙（テスト網羅・UI 表示順）。 */
export const TIME_OF_DAY_ORDER: readonly TimeOfDay[] = ['morning', 'day', 'dusk', 'night'] as const;

/** 時間帯の日本語表示名（ステータス画面・トースト等で使う）。 */
export const TIME_OF_DAY_LABELS: Record<TimeOfDay, string> = {
  morning: '朝',
  day: '昼',
  dusk: '夕',
  night: '夜',
};

/** 時間帯のアイコン（ステータス画面の見出しに使う絵文字）。 */
export const TIME_OF_DAY_ICONS: Record<TimeOfDay, string> = {
  morning: '🌅',
  day: '☀️',
  dusk: '🌆',
  night: '🌙',
};

/**
 * ローカル時（0..23）から時間帯を判定する純関数。境界は「以上〜未満」。
 *  - 朝: 5〜9 時
 *  - 昼: 9〜17 時
 *  - 夕: 17〜20 時
 *  - 夜: 20〜5 時（深夜・未明を含む）
 *
 * 時刻 0..23 の整数を期待するが、範囲外・小数も floor + mod 24 で正規化する。
 */
export function timeOfDayForHour(hour: number): TimeOfDay {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  if (h >= 5 && h < 9) return 'morning';
  if (h >= 9 && h < 17) return 'day';
  if (h >= 17 && h < 20) return 'dusk';
  return 'night';
}

/**
 * エポックミリ秒からローカル時刻の時間帯を判定する。
 *
 * `new Date(ms).getHours()` はロケールのローカル時を返す（ユーザー要望「ロケール時刻」）。
 * Date を読むだけで乱数・可変状態には触れないので、同じ ms からは必ず同じ帯になる
 * （simulate の決定性を壊さない）。テストはタイムゾーン非依存にするため timeOfDayForHour
 * を直接検証し、ここは薄い橋渡しに留める。
 */
export function timeOfDayForMs(ms: number): TimeOfDay {
  return timeOfDayForHour(new Date(ms).getHours());
}

/**
 * 客の昼型/夜型タイプ。来店係数を時間帯で振る。
 *  - diurnal（昼型）: 昼に来やすく夜は控えめ。
 *  - nocturnal（夜型）: 夜・夕に来やすく昼は控えめ。
 *  - any（無関係）: 終日フラット。
 */
export type DielType = 'diurnal' | 'nocturnal' | 'any';

/**
 * 時間帯 × 昼夜タイプ → 来店レートに掛ける係数。
 *
 * 1.0 を基準に、得意な帯で増やし不得意な帯で減らす（過度なインフレを避けるため
 * 0.5〜1.5 のレンジ。係数 0 にはしない＝「全く来ない」を作らず cozy さを保つ）。
 * any は常に 1.0。
 */
export function dielMultiplier(type: DielType, tod: TimeOfDay): number {
  if (type === 'any') return 1.0;
  if (type === 'diurnal') {
    switch (tod) {
      case 'day':
        return 1.5;
      case 'morning':
        return 1.2;
      case 'dusk':
        return 0.85;
      case 'night':
        return 0.6;
    }
  }
  // nocturnal
  switch (tod) {
    case 'night':
      return 1.5;
    case 'dusk':
      return 1.25;
    case 'morning':
      return 0.7;
    case 'day':
      return 0.55;
  }
}
